import { Draggable, Droppable } from '@hello-pangea/dnd';
import { Add } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import clsx from 'clsx';
import { DateTime } from 'luxon';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { ButtonIcons } from '@/components/ButtonIcons';
import { useUserStore } from '@/configs/store';
import { LogCard } from '@/entities/log/ui';
import { useYaMetrika } from '@/entities/yaMetrika';
import { DetailsDrawer } from '@/features/DetailsDrawer';
import { DraftLogCard, useDraftTimelogStore } from '@/features/draftTimelogs';
import { useGlobalUserFilterModel } from '@/features/globalUserFilter';
import { useIsLogsReadOnly } from '@/features/logsMode';
import { modalManagerStore } from '@/features/ModalManagerStore';
import { ReadonlyGuard } from '@/features/ReadonlyGuard';
import { TableSortOrder, viewSettingsStore } from '@/features/viewSettings';
import { useDetailsDrawer } from '@/hooks/useDetailsDrawer';
import { useTimeLogStore } from '@/pages/TimesheetsPage/model';
import { UserRole } from '@/types/user';
import { AnalyticsEvent } from '@/types/yametrika';
import { formatDurationRussian } from '@/utils/formatDurationRussian';

import { DAY_NAMES } from './config';
import { renderSkeletonColumn } from './renderSkeletonColumn';

import s from './TaskTable.module.scss';

type TaskTableProps = {
  isDropDisabled?: boolean;
};

const TaskTable = observer(({ isDropDisabled = false }: TaskTableProps) => {
  const { loadingStage, filterData: timesheetData, currentDate, lastAddId } = useTimeLogStore();
  const { filteredDraftsByDate, draftFilter, draftMinutesByDate } = useDraftTimelogStore();
  const { hideWeekends, tableSortOrder } = viewSettingsStore;
  const { sendEvent } = useYaMetrika();
  const { isOpen, selectedLog, showDetails, hideDetails, handleExited } = useDetailsDrawer();
  const { selectedUserId, isViewingSelf: isCurrentUser } = useGlobalUserFilterModel();
  const { user: currentUser } = useUserStore();
  const logRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const timesheet = timesheetData ? Object.keys(timesheetData.days) : [];

  const isAdmin = currentUser?.roles.includes(UserRole.admin);
  const isLogsReadOnly = useIsLogsReadOnly();
  const canEdit = (isCurrentUser || isAdmin) && !isLogsReadOnly;

  useHotkeys(
    ['1', '2', '3', '4', '5', '6', '7'],
    (e) => {
      e.preventDefault();
      const num = parseInt(e.key, 10);

      if (num >= 1 && num <= 7) {
        const targetDate = currentDate.set({ weekday: num as 1 | 2 | 3 | 4 | 5 | 6 | 7 });
        modalManagerStore.open('addLog', { date: targetDate, hasPreselectedDate: true });
      }

      sendEvent(AnalyticsEvent.useHotkeys);
    },
    { enabled: !isLogsReadOnly },
  );

  useEffect(() => {
    if (lastAddId) {
      const el = logRefs.current[lastAddId];

      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [lastAddId]);

  const onAddLogClick = (date: DateTime) => {
    modalManagerStore.open('addLog', { date, hasPreselectedDate: true });
  };

  return (
    <div className={clsx(s.task_table, { [s.task_table_disabled]: hideWeekends })}>
      {loadingStage.isLoading || !selectedUserId
        ? [...Array(hideWeekends ? 5 : 7)].map((_, index) =>
            renderSkeletonColumn(index, currentDate),
          )
        : timesheetData && (
            <>
              {timesheet.map((date, index) => {
                const dayDate = DateTime.fromISO(date);
                const dayName = DAY_NAMES[index];
                const logs = timesheetData.days[date];
                const drafts = isCurrentUser ? (filteredDraftsByDate[date] ?? []) : [];
                const dayDraftMinutes = isCurrentUser ? (draftMinutesByDate[date] ?? 0) : 0;
                const dayLogMinutes = logs.reduce((sum, log) => sum + log.durationMinutes, 0);
                const dayCombinedMinutes =
                  (draftFilter !== 'drafts' ? dayLogMinutes : 0) +
                  (isCurrentUser && draftFilter !== 'logs' ? dayDraftMinutes : 0);
                const today = DateTime.local();
                const isToday = dayDate.hasSame(today, 'day');
                const isWeekend = [6, 7].includes(dayDate.weekday);

                const sortedLogs = [...logs].sort((a, b) => {
                  if (tableSortOrder === TableSortOrder.TaskKey) {
                    const keyCompare = a.taskKey.localeCompare(b.taskKey);

                    if (keyCompare !== 0) return keyCompare;

                    return a.start.localeCompare(b.start);
                  }

                  const startCompare = a.start.localeCompare(b.start);

                  if (startCompare !== 0) return startCompare;

                  return a.createdAt.localeCompare(b.createdAt);
                });

                return (
                  <div
                    key={date}
                    className={clsx(s.task_table__column, {
                      [s.weekend_disabled]: isWeekend && hideWeekends && logs.length < 1,
                      [s.weekend]: isWeekend,
                      [s['today__wrapper']]: isToday,
                      [s.no_border]:
                        index === timesheet.length - 1 ||
                        (hideWeekends && index === timesheet.length - 3),
                    })}
                  >
                    <div
                      className={clsx(s['task_table__column-title'], {
                        [s['weekend__column-title']]: isWeekend,
                        [s['today__column-title']]: isToday,
                      })}
                    >
                      <div
                        className={clsx(s['task_table__column-title-day'], {
                          [s['today__column-title-day']]: isToday,
                        })}
                      >
                        <h2>{dayName}</h2>
                        <p>{dayDate.day}</p>
                      </div>
                      <div
                        className={clsx(s['task_table__column-title-action'], {
                          [s['today__column-title-action']]: isToday,
                        })}
                      >
                        {draftFilter === 'all' && dayDraftMinutes > 0 ? (
                          <Tooltip
                            title={`Залогировано: ${formatDurationRussian(dayLogMinutes)} · Черновики: ${formatDurationRussian(dayDraftMinutes)}`}
                            arrow
                          >
                            <p className={s['task_table__column-duration']}>
                              {formatDurationRussian(dayCombinedMinutes)}
                            </p>
                          </Tooltip>
                        ) : (
                          <p className={s['task_table__column-duration']}>
                            {formatDurationRussian(dayCombinedMinutes)}
                          </p>
                        )}
                        <ReadonlyGuard>
                          <ButtonIcons
                            onClick={() => onAddLogClick(dayDate)}
                            isAccentColor={isToday}
                          >
                            <Add />
                          </ButtonIcons>
                        </ReadonlyGuard>
                      </div>
                    </div>

                    <Droppable droppableId={date} isDropDisabled={isDropDisabled}>
                      {(provided) => (
                        <div
                          className={s['task_table__column-body']}
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                        >
                          {(draftFilter === 'drafts' ? 0 : sortedLogs.length) === 0 &&
                            drafts.length === 0 && (
                              <div
                                style={{
                                  fontSize: 14,
                                  color: 'var(--secondary-color)',
                                  textAlign: 'center',
                                }}
                              >
                                Нет задач
                              </div>
                            )}

                          {draftFilter !== 'drafts' &&
                            sortedLogs.map((log, index) => (
                              <Draggable
                                key={log.id}
                                draggableId={log.id.toString()}
                                index={index}
                                isDragDisabled={!canEdit}
                              >
                                {(provided) => (
                                  <div
                                    className={s['task_table__log-card']}
                                    ref={(el) => {
                                      provided.innerRef(el);
                                      logRefs.current[log.id.toString()] = el;
                                    }}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                  >
                                    <LogCard log={log} onOpenDetails={showDetails} />
                                  </div>
                                )}
                              </Draggable>
                            ))}

                          {draftFilter !== 'logs' &&
                            drafts.map((draft, draftIndex) => (
                              <Draggable
                                key={`draft-${draft.id}`}
                                draggableId={`draft-${draft.id}`}
                                index={sortedLogs.length + draftIndex}
                                isDragDisabled={!canEdit}
                              >
                                {(provided) => (
                                  <div
                                    className={s['task_table__log-card']}
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                  >
                                    <DraftLogCard draft={draft} />
                                  </div>
                                )}
                              </Draggable>
                            ))}

                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </div>
                );
              })}
            </>
          )}
      <DetailsDrawer
        open={isOpen}
        onClose={hideDetails}
        log={selectedLog}
        onExited={handleExited}
      />
    </div>
  );
});

export default TaskTable;
